import styled from 'styled-components';
import { useEffect } from 'react';

import { Guardian, GuardianRelation } from '@dreryk/egabinet-patient-frontend-api';

import { FormContextValues } from '@dreryk/react-common';
import { AutoLayout, Heading, PeselInput, RadioGroup, StyledFlex, StyledMaxWidth } from '@dreryk/react-components';

import { AddressSection } from '../../../common/EntitlementStatementForm/sections/AddressSection';
import { FirstNameField } from '../../../common/PersonForm/FirstNameField';
import { LastNameField } from '../../../common/PersonForm/LastNameField';
import { entitlementStatementsTranslationKeys, guardiansTranslationKeys } from '../../../translations';
import { useRelationCodesRadioItems } from '../../../common/GuardianModal/GuardianModal.hooks';
import { getGuardianGender } from '../../../common/GuardianModal/GuardianModalForm.utils';

import { EntitlementStatementIdentityDocumentFormSection } from './EntitlementStatementIdentityDocumentFormSection';
import { EntitlementStatementRepresentativeSelect } from './EntitlementStatementRepresentativeSelect';
import { RepresentativeEntitlementStatementCreateFormBean } from './RepresentativeEntitlementStatementCreateForm';

type EntitlementStatementRepresentativeFormBlockProps = Pick<
   FormContextValues<RepresentativeEntitlementStatementCreateFormBean>,
   'setValue' | 'getValue' | 'register' | 'watch' | 'reset'
> & {
   readonly guardians: Guardian[];
};

const FIRST_COLUMN_WIDTH = 368;
const LAYOUT_VERTICAL_SPACING = 24;
const LAYOUT_VERTICAL_GAP = `${LAYOUT_VERTICAL_SPACING}px`;

const StyledEntitlementStatementPersonalDataFormContentSection = styled.div`
   display: grid;
   grid-template-columns: ${FIRST_COLUMN_WIDTH}px 346px;
   gap: ${LAYOUT_VERTICAL_GAP} 36px;
`;
StyledEntitlementStatementPersonalDataFormContentSection.displayName =
   'StyledEntitlementStatementPersonalDataFormContentSection';

const EntitlementStatementExistingRepresentativeFormBlock = (
   props: Pick<FormContextValues<RepresentativeEntitlementStatementCreateFormBean>, 'register' | 'setValue'>
): JSX.Element => {
   const { register, setValue } = props;
   return (
      <StyledFlex gap={LAYOUT_VERTICAL_GAP} direction={'column'}>
         <EntitlementStatementIdentityDocumentFormSection
            name={'representativeIdentityDocument'}
            register={register}
            setValue={setValue}
         />
         <AddressSection register={register} prefix={'representative'} />
      </StyledFlex>
   );
};

const EntitlementStatementNewRepresentativeFormBlock = (
   props: Pick<FormContextValues<RepresentativeEntitlementStatementCreateFormBean>, 'register' | 'setValue' | 'watch'>
): JSX.Element => {
   const { register, watch, setValue } = props;

   const gender = getGuardianGender('Pesel', watch('pesel'));
   const currentRelationCode = watch('guardianRelation');
   const relationCodeItems = useRelationCodesRadioItems(gender);

   useEffect(() => {
      if (gender === 'MAN' && currentRelationCode === 'MOTHER') {
         setValue('guardianRelation', 'FATHER');
      }
      if (gender === 'WOMAN' && currentRelationCode === 'FATHER') {
         setValue('guardianRelation', 'MOTHER');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [gender]);

   return (
      <>
         <StyledFlex gap={LAYOUT_VERTICAL_GAP} direction={'column'}>
            <StyledEntitlementStatementPersonalDataFormContentSection>
               <FirstNameField register={register} />
               <LastNameField register={register} />
            </StyledEntitlementStatementPersonalDataFormContentSection>
            <StyledMaxWidth maxWidth={FIRST_COLUMN_WIDTH}>
               <PeselInput
                  controller={register('pesel')}
                  label={entitlementStatementsTranslationKeys.createModal.pesel}
                  testId={'guardianPesel'}
               />
            </StyledMaxWidth>
         </StyledFlex>
         <EntitlementStatementIdentityDocumentFormSection
            name={'representativeIdentityDocument'}
            register={register}
            setValue={setValue}
         />
         <RadioGroup<GuardianRelation>
            testId='guardianRelation'
            items={relationCodeItems}
            controller={register('guardianRelation')}
            label={guardiansTranslationKeys.relation}
            required
            horizontal
         />
         <AddressSection register={register} prefix={'representative'} />
      </>
   );
};

export const EntitlementStatementRepresentativeFormBlock = (
   props: EntitlementStatementRepresentativeFormBlockProps
): JSX.Element => {
   const { register, setValue, watch, reset } = props;

   return (
      <AutoLayout as={'div'} $direction={'vertical'}>
         <Heading
            testId={'guardianDataHeading'}
            type={'h2'}
            content={entitlementStatementsTranslationKeys.createModal.guardianData}
         />
         <AutoLayout as={'div'} $direction={'vertical'} $spacing={LAYOUT_VERTICAL_SPACING}>
            <EntitlementStatementRepresentativeSelect
               guardians={props.guardians}
               register={register}
               setValue={setValue}
               watch={watch}
               reset={reset}
            >
               {(renderProps) => (
                  <>
                     {renderProps.existingRepresentative ? (
                        <EntitlementStatementExistingRepresentativeFormBlock register={register} setValue={setValue} />
                     ) : (
                        <EntitlementStatementNewRepresentativeFormBlock
                           register={register}
                           setValue={setValue}
                           watch={watch}
                        />
                     )}
                  </>
               )}
            </EntitlementStatementRepresentativeSelect>
         </AutoLayout>
      </AutoLayout>
   );
};
